import {
    Button as AriaButton,
    Menu as AriaMenu,
    MenuItem,
    MenuTrigger,
    Popover,
} from 'react-aria-components';

import { PropsWithChildren, useState } from 'react';
import styled from 'styled-components';
import { StyledText } from '../atoms';
import { Box } from '../atoms/Box';
import Icon from '../atoms/icons/Icon';
import {
    buttonBaseStyles,
    ButtonStyle,
    ButtonVariant,
} from '../molecules/Button';
import { colors } from '../utils/colors';

const StyledPopover = styled(Popover)<{ zIndex: number }>`
    z-index: ${({ zIndex }) => zIndex} !important;
    display: inline-block;
    width: auto;
`;

const Menu = styled(AriaMenu)`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
    background-color: ${colors.white};
    border-radius: 12px;
    box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.05);
    width: auto;
    overflow: hidden; /* Prevent overflow */
    &:focus-visible {
        outline: none;
    }
`;

const Item = styled(MenuItem)<{ $variant: ButtonVariant }>`
    ${buttonBaseStyles}
    display: flex;
    cursor: pointer;
    width: 100%;
    border: none;
    box-sizing: border-box;
`;

const Dot = styled.span<{ isOpen: boolean }>`
    width: 4px;
    height: 4px;
    border-radius: 4px;
    background-color: ${({ isOpen }) =>
        isOpen ? colors.white : colors.gray600};
`;

const Circle = styled(Box)<{ isOpen: boolean }>`
    border: none;
    width: 41px;
    height: 41px;
    gap: 2px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    border-radius: 41px;
    background-color: ${({ isOpen }) =>
        isOpen ? colors.blue600 : colors.white};
    &:hover {
        transition: background-color 0.2s ease-in-out;
        background-color: ${colors.blue600};
        ${Dot} {
            background-color: ${colors.white};
        }
    }
`;

interface DropDownButtonProps {
    isOpen: boolean;
    toggleOpen: (newValue: boolean) => void;
    buttonVariant?: ButtonVariant;
    ariaLabel: string;
    stopPropagation?: boolean;
}

export function DropDownWithChevron({
    header,
    ...rest
}: DropDownButtonProps & { header: string }) {
    return (
        <DropDownButton {...rest}>
            <Box gap="12">
                <StyledText variant="body" as="span">
                    {header}
                </StyledText>
                <Icon name={rest.isOpen ? 'chevronUp' : 'chevronDown'} />
            </Box>
        </DropDownButton>
    );
}

function DropDownButton(props: PropsWithChildren<DropDownButtonProps>) {
    return (
        <ButtonStyle
            as={AriaButton}
            onClick={(e) => {
                if (props.stopPropagation) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onPress={() => props.toggleOpen(!props.isOpen)}
            $variant={props.buttonVariant || 'tertiary'}
            aria-label={props.ariaLabel}
        >
            {props.children}
        </ButtonStyle>
    );
}

export function CircleWithDots({
    isOpen,
    toggleOpen,
    ariaLabel,
    stopPropagation,
}: DropDownButtonProps) {
    return (
        <Circle
            aria-label={ariaLabel}
            isOpen={isOpen}
            onClick={(e) => {
                if (stopPropagation) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onPress={() => {
                toggleOpen(!isOpen);
            }}
            as={AriaButton}
        >
            <Dot isOpen={isOpen} />
            <Dot isOpen={isOpen} />
            <Dot isOpen={isOpen} />
        </Circle>
    );
}

export function Dropdown({
    ariaLabel,
    options,
    zIndex = 999999,
    Content,
    stopPropagation,
}: {
    ariaLabel: string;
    Content: React.ComponentType<DropDownButtonProps>;
    stopPropagation?: boolean;
    zIndex?: number;
    options: Array<{
        content: React.ReactNode;
        onAction: () => unknown;
        variant?: ButtonVariant;
        ariaLabel: string;
        id: string;
    }>;
}) {
    const [isOpen, toggleOpen] = useState(false);

    return (
        <MenuTrigger>
            <Content
                isOpen={isOpen}
                toggleOpen={toggleOpen}
                ariaLabel={ariaLabel}
                stopPropagation={stopPropagation}
            />
            <StyledPopover zIndex={zIndex}>
                <Menu>
                    {options.map((option) => (
                        <Item
                            onAction={option.onAction}
                            id={option.id}
                            key={option.ariaLabel}
                            textValue={option.ariaLabel}
                            $variant={option.variant || 'tertiary'}
                        >
                            {option.content}
                        </Item>
                    ))}
                </Menu>
            </StyledPopover>
        </MenuTrigger>
    );
}
